/* core.lineage reads and the one write.
 *
 * No `X-Lumiq-Tenant` header, here or anywhere in `src/`. Hard rule 1: the edge sets it from a
 * validated token claim, and the vite proxy injects it in dev because the proxy stands in for
 * `api-gateway`, which is the only thing permitted to.
 *
 * Every type and guard comes from `contracts/openapi/lineage.yaml` through
 * `tools/openapi_ts/generate.py`. What the generator gives this feature specifically is the shape of
 * `ImpactAnswer`: `complete` is required and `withheld` is optional, so `answer.withheld > 0` does
 * not compile until the author has decided what to do when the server did not disclose it. That is
 * the D15 amendment expressed as a type — the count is absent for a caller without the tenant-wide
 * scope, and its absence is not evidence that nothing was withheld.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isEdge,
  isImpactAnswer,
  isIngestOperation,
  isNode,
  isNodeReach,
  LINEAGE_LENSES,
} from '@/shared/api/generated/lineage';
import type {
  Edge,
  ImpactAnswer,
  IngestOperation,
  LineageLens,
  Node,
  NodeReach,
} from '@/shared/api/generated/lineage';
import { getOne, idempotencyKeyFor, post } from '@/shared/api/http';

import { reachByDataset } from '../model/presentation';
import type { ReachLookup } from '../model/presentation';

export { LINEAGE_LENSES };
export type {
  AffectedNode,
  Direction,
  Edge,
  EdgeSource,
  EdgeState,
  ImpactAnswer,
  IncompletenessReason,
  IngestOperation,
  IngestState,
  LineageLens,
  Node,
  NodeKind,
  NodeReach,
  Refusal,
  RefusalReason,
} from '@/shared/api/generated/lineage';

const NODE_KEY = ['lineage', 'node'] as const;
const REVIEW_KEY = ['lineage', 'review-queue'] as const;

/** The instant a walk answers for. `null` means "now", which the server resolves and echoes.
 *
 * Carried through the query key so switching instants cannot leave January's graph rendered under a
 * June heading. Same reasoning as the search term in the rules list key and `days` in the trend key:
 * a stale answer under a new label is worse than a spinner, and here it is worse still — the whole
 * point of `as_of` is that the graph changes.
 */
export type AsOf = string | null;

function withAsOf(path: string, asOf: AsOf): string {
  return asOf === null ? path : `${path}?as_of=${encodeURIComponent(asOf)}`;
}

/** `as_of` and `lens` together, so a caller supplying both does not have to compose two helpers
 * and get the `?`/`&` boundary right by hand.
 *
 * `lens` is omitted from the URL entirely when it is `'combined'` rather than sent as
 * `?lens=combined`: the server default is `combined`, so an explicit `combined` and an absent
 * parameter mean the same thing on the wire, and the shorter URL is what a caller shares or bookmarks.
 */
function withAsOfAndLens(path: string, asOf: AsOf, lens: LineageLens): string {
  const withInstant = withAsOf(path, asOf);
  if (lens === 'combined') return withInstant;
  const separator = withInstant.includes('?') ? '&' : '?';
  return `${withInstant}${separator}lens=${encodeURIComponent(lens)}`;
}

export function useLineageNode(nodeId: string): UseQueryResult<Node> {
  return useQuery({
    queryKey: [...NODE_KEY, nodeId],
    queryFn: () =>
      getOne(`/v1/lineage/nodes/${encodeURIComponent(nodeId)}`, isNode, 'lineage node'),
    retry: false,
  });
}

interface LineageNodeList {
  readonly nodes: readonly Node[];
}

function isNodeList(value: unknown): value is LineageNodeList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return Array.isArray(candidate['nodes']) && candidate['nodes'].every(isNode);
}

export function useLineageNodes(): UseQueryResult<LineageNodeList> {
  return useQuery({
    queryKey: [...NODE_KEY, 'list'],
    queryFn: () => getOne('/v1/lineage/nodes', isNodeList, 'lineage nodes'),
    retry: false,
  });
}

interface NodeReachList {
  readonly nodes: readonly NodeReach[];
}

function isNodeReachList(value: unknown): value is NodeReachList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  /* `.every(isNodeReach)` rather than `Array.isArray` alone. S-71 records what the loose form
     costs: a page whose every item was the wrong shape passed the guard, and the screen then
     rendered a confident verdict over data it could not read. Here it would be a count. */
  return Array.isArray(candidate['nodes']) && candidate['nodes'].every(isNodeReach);
}

/** How much lineage every node has, in one request.
 *
 * **One request for the whole index, which is the reason the route exists.** The picker lists every
 * dataset in every source with a count beside it; getting those from `impact` and `provenance` is two
 * calls per row, and this codebase has already declined that N+1 once — the executions listing
 * carries a note explaining why it shows a state rather than a violation total.
 *
 * Shares no key with `useLineageNodes` even though the two are always read together, and that is
 * deliberate: they are different routes, and S-76 records what a shared key costs when two hooks
 * stop reading one URL — whichever mounted first decided what the other got.
 */
export function useNodeReach(): UseQueryResult<NodeReachList> {
  return useQuery({
    queryKey: [...NODE_KEY, 'reach'],
    queryFn: () =>
      getOne('/v1/lineage/node-reach', isNodeReachList, 'lineage node reach'),
    retry: false,
  });
}

/** How much lineage every dataset has, as a screen consumes it.
 *
 * **Two reads joined in one place, because two screens now need the same join.** The picker worked
 * it out inline; the dataset bar needs the identical answer beside every option, and a second copy
 * would be two answers to "does this dataset have lineage" — the class this codebase records at five
 * sites. `useCursorStack`'s extraction, for the same reason.
 *
 * The status travels beside the map rather than being inferred from emptiness: an empty map means
 * "loading", "failed" or "this tenant has no lineage at all", and those are three different answers.
 * Either read pending is `loading`; either failed is `unreadable`, because both mean the screen has
 * no grounds to say a dataset has no lineage.
 */
export function useReachLookup(): ReachLookup {
  const nodes = useLineageNodes();
  const reach = useNodeReach();

  return useMemo(() => {
    if (nodes.isPending || reach.isPending) {
      return { status: 'loading', byDataset: new Map() };
    }
    if (nodes.isError || reach.isError) {
      return { status: 'unreadable', byDataset: new Map() };
    }
    return {
      status: 'ready',
      byDataset: reachByDataset(nodes.data.nodes, reach.data.nodes),
    };
  }, [
    nodes.isPending,
    nodes.isError,
    nodes.data,
    reach.isPending,
    reach.isError,
    reach.data,
  ]);
}

export function useNodeByDataset(datasetId: string | null): UseQueryResult<Node> {
  return useQuery({
    queryKey: [...NODE_KEY, 'by-dataset', datasetId],
    queryFn: () => {
      /* Narrowed rather than asserted. `enabled` below means React Query does not call this while
       * the id is null — but that is a runtime guarantee held by a sibling option, and a `!` asks
       * the reader to verify it before trusting the line. Throwing states the invariant where it
       * applies, and would be a loud bug rather than an `undefined` in a URL if `enabled` ever
       * changed. */
      if (datasetId === null || datasetId.length === 0) {
        throw new Error('useNodeByDataset was enabled with no dataset id');
      }
      return getOne(
        `/v1/lineage/nodes/by-dataset/${encodeURIComponent(datasetId)}`,
        isNode,
        'lineage node by dataset',
      );
    },
    enabled: datasetId !== null && datasetId.length > 0,
    retry: false,
  });
}

export function useImpact(
  nodeId: string,
  asOf: AsOf,
  lens: LineageLens = 'combined',
): UseQueryResult<ImpactAnswer> {
  return useQuery({
    // `lens` is in the key, for `asOf`'s own reason: switching lenses must not leave the native
    // graph rendered under a "platform" heading while the new answer is still in flight.
    queryKey: [...NODE_KEY, nodeId, 'impact', asOf, lens],
    queryFn: () =>
      getOne(
        withAsOfAndLens(
          `/v1/lineage/nodes/${encodeURIComponent(nodeId)}/impact`,
          asOf,
          lens,
        ),
        isImpactAnswer,
        'impact',
      ),
    retry: false,
  });
}

export function useProvenance(
  nodeId: string,
  asOf: AsOf,
  lens: LineageLens = 'combined',
): UseQueryResult<ImpactAnswer> {
  return useQuery({
    queryKey: [...NODE_KEY, nodeId, 'provenance', asOf, lens],
    queryFn: () =>
      getOne(
        withAsOfAndLens(
          `/v1/lineage/nodes/${encodeURIComponent(nodeId)}/provenance`,
          asOf,
          lens,
        ),
        isImpactAnswer,
        'provenance',
      ),
    retry: false,
  });
}

interface EdgeList {
  readonly node_id: string;
  readonly as_of: string;
  readonly edges: readonly Edge[];
  readonly projected_at: string | null;
}

function isEdgeList(value: unknown): value is EdgeList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['node_id'] === 'string' &&
    typeof candidate['as_of'] === 'string' &&
    Array.isArray(candidate['edges']) &&
    candidate['edges'].every(isEdge)
  );
}

/** Every edge touching a node, in **every** state.
 *
 * The inspection view, and deliberately unfiltered: a `needs_review` edge is excluded from impact
 * answers but has to be visible somewhere or it can never be reviewed. A screen that filtered here
 * would make the review queue unreachable from the thing being reviewed.
 */
export function useNodeEdges(
  nodeId: string,
  direction: 'downstream' | 'upstream',
  asOf: AsOf,
): UseQueryResult<EdgeList> {
  return useQuery({
    queryKey: [...NODE_KEY, nodeId, 'edges', direction, asOf],
    queryFn: () => {
      const base = `/v1/lineage/nodes/${encodeURIComponent(nodeId)}/edges?direction=${direction}`;
      const path = asOf === null ? base : `${base}&as_of=${encodeURIComponent(asOf)}`;
      return getOne(path, isEdgeList, 'edges');
    },
    retry: false,
  });
}

interface ReviewQueue {
  readonly edges: readonly Edge[];
  readonly projected_at: string | null;
}

function isReviewQueue(value: unknown): value is ReviewQueue {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return Array.isArray(candidate['edges']) && candidate['edges'].every(isEdge);
}

export function useReviewQueue(): UseQueryResult<ReviewQueue> {
  return useQuery({
    queryKey: REVIEW_KEY,
    queryFn: () =>
      getOne('/v1/lineage/edges/needing-review', isReviewQueue, 'review queue'),
    retry: false,
  });
}

export interface Verdict {
  readonly edgeId: string;
  readonly accepted: boolean;
}

/** Records a human verdict, and returns the **new** edge it created.
 *
 * The reviewed edge is closed and a `manual` one opened, so the response is a different edge with a
 * different id. Both queries are invalidated because both change: the queue loses an entry, and the
 * node's edge list gains one while the inference it replaces becomes closed.
 */
export function useReviewEdge(): UseMutationResult<Edge, Error, Verdict> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (verdict: Verdict) =>
      post(
        `/v1/lineage/edges/${encodeURIComponent(verdict.edgeId)}/review`,
        /* Keyed on the edge *and* the verdict. Re-submitting the same verdict is the retry case;
         * changing your mind is a different decision, and the server refuses it anyway because the
         * edge is no longer awaiting review. Keying on the edge alone would make the second attempt
         * silently indistinguishable from the first. */
        idempotencyKeyFor('review-edge', `${verdict.edgeId}:${String(verdict.accepted)}`),
        isEdge,
        'edge',
        { accepted: verdict.accepted },
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: REVIEW_KEY });
      void client.invalidateQueries({ queryKey: NODE_KEY });
    },
    retry: false,
  });
}

export function useIngest(ingestId: string): UseQueryResult<IngestOperation> {
  return useQuery({
    queryKey: ['lineage', 'ingest', ingestId],
    queryFn: () =>
      getOne(
        `/v1/lineage/ingests/${encodeURIComponent(ingestId)}`,
        isIngestOperation,
        'ingest',
      ),
    retry: false,
  });
}
